import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';

// ============ Types ============

export interface Subgroup {
  id: string;
  group_id: string;
  name: string;
  slug: string;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SubgroupInput {
  group_id: string;
  name: string;
  slug: string;
  description?: string | null;
}

// ============ Queries ============

export async function getSubgroups(): Promise<ApiResult<Subgroup[]>> {
  const { data, error } = await supabase
    .from('subgroups')
    .select('*')
    .order('name');

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data || []);
}

export async function getSubgroupsByGroupId(groupId: string): Promise<ApiResult<Subgroup[]>> {
  const { data, error } = await supabase
    .from('subgroups')
    .select('*')
    .eq('group_id', groupId)
    .order('name');

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data || []);
}

export async function getSubgroupById(id: string): Promise<ApiResult<Subgroup | null>> {
  const { data, error } = await supabase
    .from('subgroups')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data);
}

// ============ Mutations ============

export async function createSubgroup(data: SubgroupInput): Promise<ApiResult<Subgroup>> {
  const { data: subgroup, error } = await supabase
    .from('subgroups')
    .insert([data])
    .select()
    .single();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(subgroup);
}

export async function updateSubgroup(id: string, data: Partial<SubgroupInput>): Promise<ApiResult<Subgroup>> {
  const { data: subgroup, error } = await supabase
    .from('subgroups')
    .update(data)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(subgroup);
}

export async function deleteSubgroup(id: string): Promise<ApiResult<null>> {
  const { error } = await supabase
    .from('subgroups')
    .delete()
    .eq('id', id);

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(null);
}
